import React, { useState, useMemo } from 'react';
import UsePayments from './UsePayments';
import './Style/AllPayments.css';
import useUsersData from '../Auth/AllUsers';

/* =========================================
    CONVERT DATE TO IST (FIXED DOUBLE-SHIFT)
========================================= */
  const toIST = (dateString) => {
    if (!dateString) return 'Loading...';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;

    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

const AllPayments = () => {
  const { allPayments, loading } = UsePayments();
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

    const { users } = useUsersData();


  const userDetails = (phone) => {
    const targetUser = users?.find((user) => user.phone === phone);
    return {
      name: targetUser?.name || 'no',
      email: targetUser?.email || 'no',
      registerDate: targetUser?.registar_date || 'no'
    };
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!allPayments) return { totalAmount: '0.00', totalTransactions: 0, successfulPayments: 0 };
    
    const totalAmount = allPayments.reduce(
      (sum, payment) => sum + parseFloat(payment.amount || 0),
      0
    );
    const totalTransactions = allPayments.length;

    return {
      totalAmount: totalAmount.toFixed(2),
      totalTransactions,
      successfulPayments: totalTransactions
    };
  }, [allPayments]);

  // Filter payments based on search term and date range
  const filteredPayments = useMemo(() => {
    if (!allPayments) return [];
    let filtered = [...allPayments];

    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (payment) =>
          (payment.name && payment.name.toLowerCase().includes(lowercasedSearch)) ||
          (payment.phone && payment.phone.includes(searchTerm)) ||
          (payment.paymentId && payment.paymentId.toLowerCase().includes(lowercasedSearch)) ||
          (payment.buyed && payment.buyed.toLowerCase().includes(lowercasedSearch))
      );
    }

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter((payment) => new Date(payment.date) >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include full end day
      filtered = filtered.filter((payment) => new Date(payment.date) <= end);
    }

    return filtered;
  }, [allPayments, searchTerm, startDate, endDate]);

  // Export to CSV function
  const exportToCSV = () => {
    const headers = [
      'ID',
      'Name',
      'Phone',
      'Email',
      'Amount',
      'Product',
      'Payment ID',
      'Date',
      'Year',
      'Coupon'
    ];
    const csvData = filteredPayments.map((payment) => [
      payment.id || '',
      payment.name || '',
      payment.phone || '',
      payment.email || '',
      payment.amount || '',
      payment.buyed || '',
      payment.paymentId || '',
      payment.date || '',
      payment.year || '',
      payment.coupon || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading payments...</div>
      </div>
    );
  }

  if (!allPayments || allPayments.length === 0) {
    return (
      <div className="empty-state">
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
            clipRule="evenodd"
          />
        </svg>
        <h3>No payments found</h3>
        <p>There are no payment records in the system yet.</p>
      </div>
    );
  }

  return (
    <div className="payments-container">
      <header className="payments-header">
        <h2>Payment Management</h2>
        <div className="payments-stats">
          <div className="stat-card total-amount">
            <div className="stat-label">Total Amount</div>
            <div className="stat-value amount">₹{stats.totalAmount}</div>
          </div>
          <div className="stat-card total-transactions">
            <div className="stat-label">Total Transactions</div>
            <div className="stat-value">{stats.totalTransactions}</div>
          </div>
          <div className="stat-card successful">
            <div className="stat-label">Successful</div>
            <div className="stat-value">{stats.successfulPayments}</div>
          </div>
        </div>
      </header>

      <div className="payments-controls">
        <div className="search-box">
          <svg
            className="search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.3-4.3"></path>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, phone, payment ID, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <div className="date-filter">
            <input
              type="date"
              className="date-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="From Date"
            />
            <span>to</span>
            <input
              type="date"
              className="date-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="To Date"
            />
          </div>
          <button className="export-btn" onClick={exportToCSV}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* Adding overflow handling container */}
      <div className="table-container" style={{ overflowX: 'auto', width: '100%' }}>
        <table className="payments-table" style={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: '50px' }}>ID</th>
              <th style={{ width: '120px' }}>Name</th>
              <th style={{ width: '110px' }}>Phone</th>
              <th style={{ width: '180px' }}>Email</th>
              <th style={{ width: '80px' }}>Amount</th>
              <th style={{ width: '90px' }}>Product</th>
              <th style={{ width: '160px' }}>Payment ID</th>
              <th style={{ width: '150px' }}>Date</th>
              <th style={{ width: '150px' }}>Register</th>
              <th style={{ width: '70px' }}>Year</th>
              <th style={{ width: '90px' }}>Coupon</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments
              .slice()
              .reverse()
              .map((payment, index) => (
                <tr key={payment.id || index}>
                  <td>{payment.id}</td>
                  <td>{payment.name}</td>
                  <td>{payment.phone}</td>
                  <td
                    style={{
                      wordBreak: 'break-word',
                      color: '#10b981',
                      fontWeight: 'bold'
                    }}
                  >
                    {payment.email}
                  </td>
                  <td>₹{payment.amount}</td>
                  <td style={{ color: '#6366f1' }}>{payment.buyed}</td>
                  {/* Truncated payment ID to prevent huge text overflows */}
                  <td
                    style={{
                      wordBreak: 'break-all',
                      fontSize: '12px',
                      maxWidth: '160px'
                    }}
                    title={payment.paymentId}
                  >
                    {payment.paymentId}
                  </td>
                  <td>{toIST(payment.date)}</td>
                  <td><p>{userDetails(payment.phone).registerDate}</p></td>
                  <td>{payment.year || 'N/A'}</td>
                  <td>{payment.coupon || '-'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllPayments;